import { useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { fmt, bytes, CHANGE } from "./util";

const TRIGGER = { manual: "Refresh now", scheduled: "Scheduled", first: "First import" };

// Every crawl as a diff: what was added / changed / removed / held, with the per-item list on demand.
export function RunHistory({ base, runs = [] }) {
  if (!runs.length) return <div className="p-4 text-xs text-muted-foreground" data-testid="website-runs-empty">No runs yet.</div>;
  return <div data-testid="website-runs">{runs.map((r) => <RunRow key={r.id} r={r} base={base} />)}</div>;
}

function RunRow({ r, base }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const st = r.stats || {};
  const load = () => { setOpen((o) => !o); if (!detail) api.get(`${base}/runs/${r.id}`).then((x) => setDetail(x.data)).catch(() => setDetail({ changes: [] })); };
  const running = r.ok == null;
  const digest = Array.isArray(r.digest) ? r.digest : [];
  const bits = [["added", st.added], ["changed", st.changed], ["removed", st.removed], ["restored", st.restored], ["held", st.held], ["duplicates", st.duplicates]].filter(([, n]) => n);
  return (
    <div className="border-b border-sand-50" data-testid={`website-run-${r.id}`}>
      <button type="button" onClick={load} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sand-50/60">
        {open ? <ChevronDown className="h-4 w-4 text-ink/40 shrink-0" /> : <ChevronRight className="h-4 w-4 text-ink/40 shrink-0" />}
        {running ? <Loader2 className="h-4 w-4 animate-spin text-sky-600 shrink-0" /> : r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-ink flex flex-wrap items-center gap-x-2">
            <span>{fmt(r.started_at)}</span>
            <span className="text-ink/50 text-xs">{TRIGGER[r.trigger] || r.trigger}</span>
            {r.error && <span className="text-xs text-red-700 truncate">{r.error}</span>}
          </div>
          <div className="text-[11px] text-ink/55 flex flex-wrap gap-x-2">
            <span>{st.pages_seen || 0} pages · {st.docs_seen || 0} documents{st.faqs_seen ? ` · ${st.faqs_seen} FAQs` : ""}{st.bytes ? ` · ${bytes(st.bytes)} downloaded` : ""}{st.errors ? ` · ${st.errors} error${st.errors === 1 ? "" : "s"}` : ""}</span>
            {!bits.length && !running && <span className="text-emerald-700">no changes</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {!!digest.length && <span data-testid={`website-run-digest-badge-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 bg-sand-100 text-ink/70"><Sparkles className="h-2.5 w-2.5" /> summary</span>}
          {bits.map(([k, n]) => { const [l, c] = CHANGE[k]; return <span key={k} className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${c}`}>{n} {l.toLowerCase()}</span>; })}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 pl-11" data-testid={`website-run-changes-${r.id}`}>
          {!!digest.length && (
            <div className="mb-2 mt-1 rounded-xl border border-sand-200 bg-sand-50/70 p-3" data-testid={`website-run-digest-${r.id}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-ink/50">
                <Sparkles className="h-3 w-3" strokeWidth={2} /> What the concierge learned
              </div>
              <ul className="mt-1.5 space-y-1">
                {digest.map((d, i) => (
                  <li key={i} className="flex gap-2 text-xs leading-relaxed text-ink/80">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-ink/30" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!detail ? <div className="text-xs text-muted-foreground py-1">Loading…</div>
            : !detail.changes?.length ? <div className="text-xs text-muted-foreground py-1">Nothing changed in this run.</div>
            : detail.changes.map((c, i) => { const [l, cls] = CHANGE[c.change] || [c.change, "bg-sand-100 text-ink/60"]; return (
              <div key={i} className="flex items-center gap-2 py-1 text-xs border-b border-sand-50 last:border-0">
                <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${cls}`}>{l}</span>
                <span className="text-ink/50 shrink-0 w-16">{c.kind === "faq" ? "FAQ" : c.kind === "page" ? "Page" : "Document"}</span>
                <a href={c.url} target="_blank" rel="noreferrer" className="truncate text-ink hover:underline" title={c.url}>{c.title || c.url}</a>
                {c.detail && <span className="text-ink/45 truncate">· {c.detail}</span>}
                {c.duplicate_of_title && <span className="text-ink/45 truncate">· same as “{c.duplicate_of_title}”</span>}
              </div>); })}
        </div>
      )}
    </div>
  );
}
